import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Typography,
  Box,
  Paper,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  IconButton,
  Breadcrumbs,
  Link,
  Chip,
  Snackbar,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider
} from '@mui/material';
import {
  Folder as FolderIcon,
  Description as FileIcon,
  Download as DownloadIcon,
  Backup as BackupIcon,
  Restore as RestoreIcon,
  Home as HomeIcon,
  CloudUpload as UploadIcon,
  Close as CloseIcon,
  History as HistoryIcon
} from '@mui/icons-material';

const API_BASE_URL = 'https://test2.planfix-to-syrve.com';

const DrawingsManager = () => {
  const [folders, setFolders] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  
  const [replaceDialogOpen, setReplaceDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  
  
  const [backupsDialogOpen, setBackupsDialogOpen] = useState(false);
  const [backups, setBackups] = useState([]);
  const [loadingBackups, setLoadingBackups] = useState(false);
  const [currentBackupFile, setCurrentBackupFile] = useState(null);
  
  
  const fetchFolders = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await axios.get(`${API_BASE_URL}/api/drawings/folders`);
      if (response.data.success) {
        setFolders(response.data.data);
      } else {
        setError('Помилка завантаження папок');
      }
    } catch (err) {
      console.error('Error fetching folders:', err);
      setError('Помилка завантаження папок: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  }, []);
  
  
  const fetchFiles = useCallback(async (folderName) => {
    if (!folderName) return;
    
    try {
      setLoading(true);
      setError('');
      const response = await axios.get(`${API_BASE_URL}/api/drawings/folders/${encodeURIComponent(folderName)}/files`);
      if (response.data.success) {
        setFiles(response.data.data);
      } else {
        setError('Помилка завантаження файлів');
      }
    } catch (err) {
      console.error('Error fetching files:', err);
      setError('Помилка завантаження файлів: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  }, []);
  
  
  const fetchBackups = useCallback(async (folderName, fileName) => {
    try {
      setLoadingBackups(true);
      const response = await axios.get(`${API_BASE_URL}/api/drawings/backups`, {
        params: { folderName, fileName }
      });
      if (response.data.success) {
        setBackups(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching backups:', err);
      setError('Помилка завантаження бекапів');
    } finally {
      setLoadingBackups(false);
    }
  }, []);
  
  useEffect(() => {
    fetchFolders();
  }, [fetchFolders]);
  
  
  const handleFolderClick = (folder) => {
    setSelectedFolder(folder);
    fetchFiles(folder);
  };
  
  
  const handleBackToFolders = () => {
    setSelectedFolder(null);
    setFiles([]);
  };
  
  
  const handleDownload = async (fileName) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/drawings/download`, {
        params: {
          folderName: selectedFolder,
          fileName: fileName
        },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      setSuccess(`Файл ${fileName} завантажено`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error downloading file:', err);
      setError('Помилка завантаження файлу');
    }
  };
  
  
  const handleOpenReplaceDialog = (file) => {
    setSelectedFile(file);
    setReplaceDialogOpen(true);
    setUploadFile(null);
  };
  
  
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file && (file.name.endsWith('.xls') || file.name.endsWith('.xlsx'))) {
      setUploadFile(file);
    } else {
      setError('Будь ласка, виберіть файл .xls або .xlsx');
    }
  };
  
  
  const handleReplaceFile = async () => {
    if (!uploadFile) {
      setError('Виберіть файл для завантаження');
      return;
    }
    
    try {
      setUploading(true);
      
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('folderName', selectedFolder);
      formData.append('originalFileName', selectedFile.name);
      
      const response = await axios.post(`${API_BASE_URL}/api/drawings/replace-file`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (response.data.success) {
        setSuccess(`Файл ${selectedFile.name} успішно замінено`);
        setReplaceDialogOpen(false);
        setUploadFile(null);
        setSelectedFile(null);
        
        fetchFiles(selectedFolder);
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(response.data.error || 'Помилка заміни файлу');
      }
    } catch (err) {
      console.error('Error replacing file:', err);
      setError('Помилка при заміні файлу');
    } finally {
      setUploading(false);
    }
  };
  
  
  const handleOpenBackupsDialog = async (file) => {
    setCurrentBackupFile(file);
    await fetchBackups(selectedFolder, file.name);
    setBackupsDialogOpen(true);
  };
  
  
  const handleRestoreFromBackup = async (backupName) => {
    if (window.confirm(`Відновити файл ${currentBackupFile.name} з бекапу ${backupName}?`)) {
      try {
        const response = await axios.post(`${API_BASE_URL}/api/drawings/restore-backup`, {
          folderName: selectedFolder,
          fileName: currentBackupFile.name,
          backupName: backupName
        });
        
        if (response.data.success) {
          setSuccess(`Файл ${currentBackupFile.name} відновлено з бекапу`);
          setBackupsDialogOpen(false);
          fetchFiles(selectedFolder);
          setTimeout(() => setSuccess(''), 3000);
        } else {
          setError(response.data.error || 'Помилка відновлення');
        }
      } catch (err) {
        console.error('Error restoring from backup:', err);
        setError('Помилка при відновленні');
      }
    }
  };
  
  
  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };
  
  
  const formatDate = (date) => {
    if (!date) return 'Невідомо';
    return new Date(date).toLocaleString('uk-UA');
  };
  
  
  const renderFolders = () => (
    <Grid container spacing={3}>
      {folders.map((folder, index) => (
        <Grid item xs={12} sm={6} md={4} lg={3} key={folder + index}>
          <Card 
            sx={{ 
              cursor: 'pointer', 
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: 6
              }
            }}
            onClick={() => handleFolderClick(folder)}
          >
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <FolderIcon sx={{ fontSize: 48, color: '#ffc107' }} />
                <Typography variant="h6" component="div" sx={{ wordBreak: 'break-word' }}>
                  {folder}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
  
  
  const renderFiles = () => (
    <Box>
      <Box display="flex" alignItems="center" gap={2} mb={3} flexWrap="wrap">
        <Button variant="outlined" onClick={handleBackToFolders} startIcon={<HomeIcon />}>
          До списку папок
        </Button>
        <Typography variant="h5">
          {selectedFolder}
        </Typography>
        <Chip label={`Файлів: ${files.length}`} color="primary" />
      </Box>
      
      <Grid container spacing={2}>
        {files.map((file, index) => (
          <Grid item xs={12} sm={6} md={4} key={file.name + index}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <FileIcon sx={{ color: '#4caf50' }} />
                  <Typography variant="body2" color="textSecondary">
                    {file.name?.split('.').pop() || 'xls'}
                  </Typography>
                </Box>
                <Typography variant="subtitle1" sx={{ wordBreak: 'break-word', fontWeight: 'bold' }}>
                  {file.name}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {formatFileSize(file.size)} • {formatDate(file.modified)}
                </Typography>
              </CardContent>
              <CardActions>
                <Button 
                  size="small" 
                  startIcon={<DownloadIcon />}
                  onClick={() => handleDownload(file.name)}
                >
                  Завантажити
                </Button>
                <Button 
                  size="small" 
                  startIcon={<UploadIcon />}
                  color="warning"
                  onClick={() => handleOpenReplaceDialog(file)}
                >
                  Замінити
                </Button>
                <IconButton 
                  size="small" 
                  title="Історія бекапів"
                  onClick={() => handleOpenBackupsDialog(file)}
                >
                  <HistoryIcon fontSize="small" />
                </IconButton>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>
      
      {files.length === 0 && !loading && (
        <Box textAlign="center" py={4}>
          <Typography color="textSecondary">В папці немає файлів</Typography>
        </Box>
      )}
    </Box>
  );
  
  return (
    <Box sx={{ p: 3, maxWidth: 1200, margin: '0 auto', mt: 8 }}>
   
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      
      <Snackbar 
        open={!!success} 
        autoHideDuration={3000} 
        onClose={() => setSuccess('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setSuccess('')}>
          {success}
        </Alert>
      </Snackbar>
      
      {loading && !selectedFolder ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : selectedFolder ? (
        loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : (
          renderFiles()
        )
      ) : (
        folders.length === 0 && !loading ? (
          <Box textAlign="center" py={4}>
            <Typography color="textSecondary">Немає доступних папок</Typography>
          </Box>
        ) : (
          renderFolders()
        )
      )}
      
      {}
      <Dialog open={replaceDialogOpen} onClose={() => setReplaceDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Заміна файлу
          <IconButton onClick={() => setReplaceDialogOpen(false)} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              Ви замінюєте файл: <strong>{selectedFile?.name}</strong>
            </Alert>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Оберіть новий файл (.xls або .xlsx). Він буде збережений з назвою {selectedFile?.name}
            </Typography>
            <Button
              variant="outlined"
              component="label"
              fullWidth
              sx={{ mt: 2 }}
              startIcon={<UploadIcon />}
            >
              {uploadFile ? uploadFile.name : 'Вибрати файл'}
              <input
                type="file"
                hidden
                accept=".xls,.xlsx"
                onChange={handleFileSelect}
              />
            </Button>
            {uploadFile && (
              <Typography variant="caption" color="success.main" sx={{ mt: 1, display: 'block' }}>
                Вибрано: {uploadFile.name} ({(uploadFile.size / 1024).toFixed(1)} KB)
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReplaceDialogOpen(false)} disabled={uploading}>
            Скасувати
          </Button>
          <Button 
            onClick={handleReplaceFile} 
            variant="contained" 
            color="warning"
            disabled={!uploadFile || uploading}
          >
            {uploading ? <CircularProgress size={24} /> : 'Замінити'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {}
      <Dialog open={backupsDialogOpen} onClose={() => setBackupsDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Резервні копії: {currentBackupFile?.name}
          <IconButton onClick={() => setBackupsDialogOpen(false)} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {loadingBackups ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress size={30} />
            </Box>
          ) : backups.length === 0 ? (
            <Typography textAlign="center" py={4} color="textSecondary">
              Немає резервних копій для цього файлу
            </Typography>
          ) : (
            <List>
              {backups.map((backup, idx) => (
                <React.Fragment key={backup.name + idx}>
                  <ListItem>
                    <ListItemText
                      primary={backup.name}
                      secondary={`Дата: ${formatDate(backup.date)} • Розмір: ${formatFileSize(backup.size)}`}
                    />
                    <ListItemSecondaryAction>
                      <Button
                        size="small"
                        startIcon={<RestoreIcon />}
                        onClick={() => handleRestoreFromBackup(backup.name)}
                      >
                        Відновити
                      </Button>
                    </ListItemSecondaryAction>
                  </ListItem>
                  {idx < backups.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBackupsDialogOpen(false)}>Закрити</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DrawingsManager;
